import React, { Suspense, lazy, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button } from '@mui/material';
import { useAuth } from '@/core/contexts/AuthContext';
import LoadingScreen from '@/components/common/LoadingScreen';
import ErrorBoundary from '@/components/common/ErrorBoundary';

// Lazy load dashboards - only the one needed will be downloaded
const ManagerDashboard = lazy(() => import('@/features/dashboard/manager/ManagerDashboard'));
const InvestorDashboard = lazy(() => import('@/features/dashboard/investor/InvestorDashboard'));
const AdminDashboard = lazy(() => import('@/features/dashboard/admin/AdminDashboard'));

const ChunkLoadErrorFallback = ({ role, resetError }) => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '100vh',
    p: 4,
    textAlign: 'center'
  }}>
    <h2>Failed to Load {role} Dashboard</h2>
    <p>There was a problem loading the dashboard. This could be due to a network issue.</p>
    <Button 
      variant="contained"
      onClick={resetError}
      sx={{ mt: 2 }}
    >
      Try Again
    </Button>
    <Button 
      variant="contained"
      color="inherit"
      onClick={() => window.location.reload()}
      sx={{ mt: 1 }}
    >
      Reload Page
    </Button>
  </Box>
);

ChunkLoadErrorFallback.propTypes = {
  role: PropTypes.string.isRequired,
  resetError: PropTypes.func.isRequired
};

const RoleDashboardLoader = () => {
  const { user } = useAuth();
  
  const DashboardComponent = useMemo(() => {
    switch(user?.role) {
      case 'manager':
        return ManagerDashboard;
      case 'investor':
        return InvestorDashboard;
      case 'admin':
        return AdminDashboard;
      default:
        return null;
    }
  }, [user?.role]);

  if (!DashboardComponent) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        Invalid user role: {user?.role}
      </Box>
    );
  }

  return (
    <ErrorBoundary 
      fallback={(props) => (
        <ChunkLoadErrorFallback 
          role={user.role} 
          {...props} 
        />
      )}
    >
      <Suspense fallback={<LoadingScreen message={`Loading ${user.role} dashboard...`} />}>
        <DashboardComponent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default RoleDashboardLoader;
